import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ClockIcon, MinusIcon, PlusIcon } from "@heroicons/react/outline";
import Card from "components/Card";
import { cx } from "utils";
import { addDays, addWeeks, addMonths, format, formatDistanceToNow } from "date-fns";

import type { ScheduleFormValue } from "pages/crawler/types";
import { cleanSchedule } from "pages/crawler/utils/crawlerFormUtils";

const getNextJobDates = (interval: string) => {
  const now = new Date();
  const dates: Date[] = [];

  switch (interval) {
    case "hourly":
      // Start from the next full hour
      const nextHour = new Date(now);
      nextHour.setMinutes(0, 0, 0);
      nextHour.setHours(nextHour.getHours() + 1);

      for (let i = 0; i < 3; i++) {
        dates.push(new Date(nextHour.getTime() + i * 60 * 60 * 1000));
      }
      break;

    case "daily":
      // Start from tomorrow at midnight
      const nextDay = new Date(now);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);

      for (let i = 0; i < 3; i++) {
        dates.push(addDays(nextDay, i));
      }
      break;

    case "weekly":
      // Start from next week same day at midnight
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(0, 0, 0, 0);

      for (let i = 0; i < 3; i++) {
        dates.push(addWeeks(nextWeek, i));
      }
      break;

    case "monthly":
      // Start from 1st of next month at midnight
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(1);
      nextMonth.setHours(0, 0, 0, 0);

      for (let i = 0; i < 3; i++) {
        dates.push(addMonths(nextMonth, i));
      }
      break;

    case "once":
      // For "scrape once", show next immediate execution (now)
      dates.push(now);
      break;

    default:
      // No predictable schedule (disabled/custom)
      break;
  }

  return dates;
};

const NextJobsDisclosure = ({ schedule }: { schedule: ScheduleFormValue }) => {
  const cleaned = cleanSchedule(schedule);
  const interval = cleaned.interval;
  const isSchedulingDisabled = interval === "disabled" || cleaned.enabled === false;
  const isScrapeOnce = interval === "once";
  const upcomingRuns = getNextJobDates(interval);

  return (
    <div className="col-span-full">
      <Card className="border border-[#e5eaee] bg-[#ecf0f3] px-2 py-1">
        <Disclosure>
          {({ open }) => (
            <>
              <DisclosureButton
                className={cx(
                  "flex flex-row items-center justify-between w-full transition-colors duration-50"
                )}
              >
                <div className="flex gap-2 items-center">
                  <ClockIcon className="h-4 w-4" />
                  {isScrapeOnce ? "Next Job" : "Next 3 Jobs"}
                </div>
                {open ? (
                  <MinusIcon className="h-4 w-4" />
                ) : (
                  <PlusIcon className="h-4 w-4" />
                )}
              </DisclosureButton>

              <DisclosurePanel
                className="flex flex-col"
                style={{ marginTop: 0 }}
              >
                <div className="space-y-2">
                  {isSchedulingDisabled ? (
                    <div className="text-sm text-gray-500">
                      Scheduling is disabled. This crawler will not run automatically.
                    </div>
                  ) : upcomingRuns.length === 0 ? (
                    <div className="text-sm text-gray-500">
                      No scheduled jobs
                    </div>
                  ) : (
                    upcomingRuns.map((date, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="text-sm">{format(date, "MMM d HH:mm")}</div>
                        <div className="text-xs text-gray-500">
                          {isScrapeOnce
                            ? "On schedule"
                            : formatDistanceToNow(date, { addSuffix: true })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
      </Card>
    </div>
  );
};

export default NextJobsDisclosure;
